import { loan_application_api } from '@axo/shared/data-access/api';
import { loan_application } from '@axo/shared/data-access/types';
import { Handler } from '../api-orchestrator';
import { IPreconditions } from '../types/IPreconditions';

interface IDependencies {
  postChild: typeof loan_application_api.postChild;
  patchChild: typeof loan_application_api.patchChild;
  deleteChild: typeof loan_application_api.deleteChild;
}

export const childHandlerFactory =
  ({
    postChild,
    patchChild,
    deleteChild,
  }: IDependencies): Handler<loan_application.Child, IPreconditions> =>
  async ({
    data,
    preconditions: {
      customerId,
      customerJWT,
      loanApplicationId,
      ids,
      children,
    },
    baseUrl,
    frontendId,
  }) => {
    if (!customerId || !customerJWT || !loanApplicationId) {
      return { didSync: false };
    }

    const id = ids.children?.[frontendId];
    const storedChild = children?.find((child) => child.ID === id);
    const api = { url: { service: baseUrl }, token: customerJWT };

    if (id) {
      if (data.Age === null) {
        await deleteChild(api)(loanApplicationId, id);

        delete ids.children?.[frontendId];

        return {
          didSync: true,
          newPreconditions: {
            ids,
            children: children?.filter((child) => child.ID !== id),
          },
        };
      }

      if (storedChild?.Age === data.Age) {
        return {
          didSync: false,
        };
      }

      const patched = await patchChild(api)(loanApplicationId, id, data);

      const updatedChildren = children?.map((child) => {
        if (child.ID === id) {
          return patched;
        }

        return child;
      });

      return {
        didSync: true,
        newPreconditions: {
          children: updatedChildren,
        },
      };
    }

    if (data.Age === null) {
      return {
        didSync: false,
      };
    }

    const child = await postChild(api)(loanApplicationId, data);

    const updatedChildren = children ? [...children, child] : [child];

    return {
      didSync: true,
      newPreconditions: {
        ids: {
          ...ids,
          children: {
            ...ids.children,
            [frontendId]: child.ID,
          },
        },
        children: updatedChildren,
      },
    };
  };
